import { create } from "zustand";

/**
 * 장바구니에서 사용자가 고른 쿠폰 선택 상태.
 *
 * - `auto` — 아직 손대지 않음. **쓸 수 있는 쿠폰을 전부 자동 적용**한다.
 * - `none` — 사용자가 명시적으로 "쿠폰 사용 안 함"을 골랐다.
 *
 * `auto` 와 `none` 을 굳이 구분하는 이유: 둘 다 "적용된 쿠폰이 없다"가 아니다. `auto` 는
 * 카트 내용이 바뀌면 다시 계산하지만, `none` 은 사용자 의사이므로 유지해야 한다.
 * `applied` 가 비었는지 하나로 뭉치면 카트를 담을 때마다 사용자가 끈 쿠폰이 되살아난다.
 *
 * ⚠️ 종전에 있던 `picked`(특정 쿠폰 1장 선택)는 **일부러 없앴다.** 쿠폰이 상품 SKU 에 매이면서
 * (mgmt#1239) 여러 장이 서로 다른 라인에 동시에 적용되므로, 한 장만 고르는 상태는 남은 라인의
 * 할인을 조용히 버린다. 표현할 수 없는 상태는 만들지 않는다.
 */
export type CartCouponSelection = { kind: "auto" } | { kind: "none" };

type CartCouponStore = {
  selection: CartCouponSelection;
  setSelection: (selection: CartCouponSelection) => void;
};

/**
 * ⚠️ **일부러 persist 하지 않는다** (cart-store 와 다른 점).
 *
 * `none` 이 localStorage 에 남으면 그 브라우저는 이후 방문에서 **아무 안내 없이 할인을 못 받는다**
 * — 사용자는 예전에 한 번 끈 걸 기억하지 못하고 우리는 매출을 조용히 잃는다. 세션 한정으로 두면
 * 새로고침 시 `auto`(= 쓸 수 있는 쿠폰 전부 적용)로 복귀하므로 실패 방향이 고객에게 유리하다.
 *
 * #6179 B 이후 「사용 안 함」은 **탭 세션 동안** 서버(`couponOptOut`)와 함께 유지된다 — GE 장바구니 견적이 서버 선택을
 * 따르므로, 페이지마다 화면만 `auto` 로 돌아가면 화면과 GE 합계가 갈린다. 새 탭·새 세션은 여전히 `auto` 로 시작한다
 * (`src/libs/global-e/cart-prep.ts` `readStoredOptOut`).
 */
export const useCartCouponStore = create<CartCouponStore>((set) => ({
  selection: { kind: "auto" },
  setSelection: (selection) => set({ selection }),
}));
